import { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
    FaAt, FaCheck, FaCheckDouble, FaCrown, FaDownload, FaEnvelope, FaFileAlt, FaFolderOpen, FaImage,
    FaPaperclip, FaPaperPlane, FaSearch, FaSignOutAlt, FaThumbtack, FaTimes, FaTrashAlt, FaBroom,
    FaUserMinus, FaUserPlus, FaUsers, FaUsersCog, FaPhone, FaMapMarkerAlt, FaIdBadge, FaBars,
} from 'react-icons/fa';
import { AppModal } from '../Modal/_index';
import { AppButton } from '../Button/_index';
import { AppLightbox } from '../Gallery/_index';
import { FieldCheckbox, FieldInput } from '../Form/_index';

const BLOCK = 'LStudio__app_chat';
const TOOLTIP_ID = 'app-chat-add-participant-tooltip';

const onInitials = (name = '') => String(name)
    .trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase()).join('') || '?';

const onTime = (value) => {
    const DATE = value ? new Date(value) : null;
    if (!DATE || Number.isNaN(DATE.getTime())) return '';
    return DATE.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
};

const onFormatBytes = (bytes = 0) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const UNITS = ['B', 'KB', 'MB', 'GB'];
    const INDEX = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), UNITS.length - 1);
    return `${(bytes / (1024 ** INDEX)).toFixed(INDEX === 0 ? 0 : 1)} ${UNITS[INDEX]}`;
};

const onStatus = (status) => {
    if (status === 'read') return { label: 'Leído', icon: <FaCheckDouble />, className: 'read' };
    if (status === 'delivered') return { label: 'Entregado', icon: <FaCheckDouble />, className: 'delivered' };
    return { label: 'Enviado', icon: <FaCheck />, className: 'sent' };
};


const onEscapeRegExp = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const onRenderMentionedText = (text, members = [], isGroup = false) => {
    const VALUE = String(text ?? '');
    if (!VALUE || !isGroup) return VALUE;
    const NAMES = members.map((user) => String(user?.name ?? '').trim().split(/\s+/)[0])
        .filter(Boolean);
    if (NAMES.length === 0) return VALUE;
    const REGEX = new RegExp(`(@(?:${NAMES.map(onEscapeRegExp).join('|')}))(?=\\b|\\s|$)`, 'gi');
    const TARGETS = new Set(NAMES.map((name) => `@${name}`.toLowerCase()));
    return VALUE.split(REGEX).map((part, index) => (
        TARGETS.has(String(part).toLowerCase())
            ? <strong key={`${part}-${index}`} className={`${BLOCK}__mention_text`}>{part}</strong>
            : part
    ));
};

const onAttachmentFromFile = (file) => ({
    id: `${file.name}-${file.size}-${file.lastModified}`,
    name: file.name, size: file.size, type: file.type || 'application/octet-stream',
    url: URL.createObjectURL(file),
});

const onIsImageAttachment = (attachment) => {
    const TYPE = String(attachment?.type ?? '').toLowerCase();
    const NAME = String(attachment?.name ?? '').toLowerCase();
    return TYPE.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(NAME);
};

const onAttachmentSignature = (attachment) => {
    const NAME = String(attachment?.name ?? '').trim().toLowerCase();
    return `${NAME}::${Number(attachment?.size ?? 0)}`;
};

const onIsGroup = (conversation) => {
    return conversation?.type === 'group' || (conversation?.memberIds?.length ?? 0) > 2;
};

const onDateKey = (value) => {
    const DATE = value ? new Date(value) : null;
    if (!DATE || Number.isNaN(DATE.getTime())) return 'sin-fecha';
    return `${DATE.getFullYear()}-${String(DATE.getMonth() + 1).padStart(2, '0')}-${String(DATE.getDate()).padStart(2, '0')}`;
};

const onDateGroupLabel = (value) => {
    const DATE = value ? new Date(value) : null;
    if (!DATE || Number.isNaN(DATE.getTime())) return 'Sin fecha';

    const NOW = new Date();
    const TODAY = new Date(NOW.getFullYear(), NOW.getMonth(), NOW.getDate());
    const TARGET = new Date(DATE.getFullYear(), DATE.getMonth(), DATE.getDate());
    const DIFF = Math.round((TODAY.getTime() - TARGET.getTime()) / 86400000);
    const DATE_TEXT = DATE.toLocaleDateString('es-AR', { day: '2-digit', month: 'long' });

    if (DIFF === 0) return 'Hoy';
    if (DIFF === 1) return 'Ayer';
    if (DIFF === 2) return 'Anteayer';
    if (DIFF > 2 && DIFF < 7) return DATE.toLocaleDateString('es-AR', { weekday: 'long', day: '2-digit', month: 'long' });
    if (DIFF >= 7 && DIFF < 14) return `Semana pasada · ${DATE_TEXT}`;
    return DATE.toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
};

export const AppChat = ({
    chat,
    currentUserId = null,
    users = [],
    title = 'Conversaciones',
    emptyText = 'Seleccioná una conversación para comenzar.',
}) => {
    const [useNameSearchState, setNameSearchState] = useState('');
    const [useNameConversationFilterState, setNameConversationFilterState] = useState('all');
    const [useNameMessageState, setNameMessageState] = useState('');
    const [useNameAttachmentsState, setNameAttachmentsState] = useState([]);
    const [useNamePanelState, setNamePanelState] = useState(null);
    const [useNameGroupNameState, setNameGroupNameState] = useState('');
    const [useNameGroupImageState, setNameGroupImageState] = useState(null);
    const [useNameSelectedUsersState, setNameSelectedUsersState] = useState([]);
    const [useNameRenameState, setNameRenameState] = useState('');
    const [useNameInviteEmailState, setNameInviteEmailState] = useState('');
    const [useNameComposerNoticeState, setNameComposerNoticeState] = useState('');
    const [useNameManageNoticeState, setNameManageNoticeState] = useState('');
    const [useNamePreviewImageState, setNamePreviewImageState] = useState(null);
    const [useNameDeleteMessageState, setNameDeleteMessageState] = useState(null);
    const [useNameClearChatState, setNameClearChatState] = useState(false);
    const [useNameDeleteConversationState, setNameDeleteConversationState] = useState(false);
    const [useNameParticipantProfileState, setNameParticipantProfileState] = useState(null);
    const [useNameMentionedUsersState, setNameMentionedUsersState] = useState([]);
    const [useNameMentionQueryState, setNameMentionQueryState] = useState(null);
    const [useNameMentionRangeState, setNameMentionRangeState] = useState(null);
    const [useNameMobilePanelState, setNameMobilePanelState] = useState(null);
    const REF__useRefMessages = useRef(null);
    const REF__useRefFileInput = useRef(null);
    const REF__useRefGroupImageInput = useRef(null);
    const REF__useRefComposer = useRef(null);

    const memo__usersMapMemo = useMemo(() => {
        return new Map(users.map((user) => [user.id, user]));
    }, [users]);

    const onConversationTitle = (conversation) => {
        if (conversation?.name) return conversation.name;
        const OTHERS = (conversation?.memberIds ?? [])
            .filter((id) => id !== currentUserId)
            .map((id) => memo__usersMapMemo.get(id)?.name).filter(Boolean);
        return OTHERS.join(', ') || 'Conversación';
    };

    const memo__conversationsMemo = useMemo(() => {
        const QUERY = useNameSearchState.trim().toLowerCase();
        const FILTERED = chat.conversations.filter((conversation) => {
            if (conversation.archived) return false;
            const NAME = String(onConversationTitle(conversation)).toLowerCase();
            const MEMBER_NAMES = (conversation.memberIds ?? [])
                .map((id) => memo__usersMapMemo.get(id)?.name ?? '').join(' ').toLowerCase();
            const MATCH_SEARCH = !QUERY || NAME.includes(QUERY) || MEMBER_NAMES.includes(QUERY);
            const MATCH_FILTER = useNameConversationFilterState === 'all'
                || (useNameConversationFilterState === 'unread' && Number(conversation.unread ?? 0) > 0)
                || (useNameConversationFilterState === 'mentions' && onIsGroup(conversation) && Number(conversation.mentions ?? 0) > 0);
            return MATCH_SEARCH && MATCH_FILTER;
        });

        return [...FILTERED].sort((a, b) => {
            const PIN = Number(Boolean(b.pinned)) - Number(Boolean(a.pinned));
            if (PIN !== 0) return PIN;
            const A_LAST = a.messages?.[a.messages.length - 1]?.createdAt ?? '';
            const B_LAST = b.messages?.[b.messages.length - 1]?.createdAt ?? '';
            return String(B_LAST).localeCompare(String(A_LAST));
        });
    }, [chat.conversations, memo__usersMapMemo, useNameSearchState, useNameConversationFilterState]);

    const memo__filterCountsMemo = useMemo(() => ({
        all: chat.conversations.filter((conversation) => !conversation.archived).length,
        unread: chat.conversations.filter((conversation) => !conversation.archived && Number(conversation.unread ?? 0) > 0).length,
        mentions: chat.conversations.filter((conversation) => !conversation.archived && onIsGroup(conversation) && Number(conversation.mentions ?? 0) > 0).length,
    }), [chat.conversations]);

    const memo__conversationSectionsMemo = useMemo(() => ({
        groups: memo__conversationsMemo.filter(onIsGroup),
        direct: memo__conversationsMemo.filter((conversation) => !onIsGroup(conversation)),
    }), [memo__conversationsMemo]);

    const memo__selectedMembersMemo = useMemo(() => {
        return (chat.selectedConversation?.memberIds ?? [])
            .map((id) => memo__usersMapMemo.get(id)).filter(Boolean);
    }, [chat.selectedConversation, memo__usersMapMemo]);

    const memo__availableUsersMemo = useMemo(() => {
        const CURRENT = new Set(chat.selectedConversation?.memberIds ?? []);
        return users.filter((user) => !CURRENT.has(user.id));
    }, [chat.selectedConversation, users]);

    const memo__isGroupMemo = onIsGroup(chat.selectedConversation);
    const memo__adminIdsMemo = chat.selectedConversation?.adminIds ?? [];
    const memo__isCurrentUserAdminMemo = memo__isGroupMemo && memo__adminIdsMemo.includes(currentUserId);
    const memo__hasLeftGroupMemo = memo__isGroupMemo && (chat.selectedConversation?.leftBy ?? []).includes(currentUserId);
    const memo__typingUsersMemo = useMemo(() => {
        const IDS = chat.selectedConversation?.typingUserIds ?? [];
        return IDS.filter((id) => id !== currentUserId)
            .map((id) => memo__usersMapMemo.get(id)).filter(Boolean);
    }, [chat.selectedConversation?.typingUserIds, currentUserId, memo__usersMapMemo]);
    const memo__pinnedMessageMemo = useMemo(() => {
        const PINNED_ID = chat.selectedConversation?.pinnedMessageId;
        if (!PINNED_ID) return null;
        return (chat.selectedConversation?.messages ?? []).find((message) => message.id === PINNED_ID) ?? null;
    }, [chat.selectedConversation?.pinnedMessageId, chat.selectedConversation?.messages]);
    const memo__mentionCandidatesMemo = useMemo(() => {
        const QUERY = String(useNameMentionQueryState ?? '').trim().toLowerCase();
        if (!memo__isGroupMemo || useNameMentionQueryState === null) return [];
        const SELECTED_IDS = new Set(useNameMentionedUsersState.map((user) => user.id));
        return memo__selectedMembersMemo
            .filter((user) => user.id !== currentUserId)
            .filter((user) => {
                const NAME = String(user.name ?? '').toLowerCase();
                const ROLE = String(user.role ?? '').toLowerCase();
                return !QUERY || NAME.includes(QUERY) || ROLE.includes(QUERY);
            })
            .sort((a, b) => Number(SELECTED_IDS.has(a.id)) - Number(SELECTED_IDS.has(b.id)))
            .slice(0, 6);
    }, [currentUserId, memo__isGroupMemo, memo__selectedMembersMemo, useNameMentionQueryState, useNameMentionedUsersState]);
    const memo__filesByDateMemo = useMemo(() => {
        const GROUPS = new Map();
        (chat.selectedConversation?.messages ?? []).filter((message) => !message.archived && !(message.hiddenFor ?? []).includes(currentUserId)).forEach((message) => {
            const SENDER = memo__usersMapMemo.get(message.senderId)?.name ?? 'Usuario';
            const DATE = message.createdAt ? new Date(message.createdAt) : new Date();
            const LABEL = Number.isNaN(DATE.getTime())
                ? 'Sin fecha'
                : DATE.toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
            (message.attachments ?? []).forEach((attachment) => {
                if (!GROUPS.has(LABEL)) GROUPS.set(LABEL, []);
                GROUPS.get(LABEL).push({ ...attachment, senderName: SENDER, createdAt: message.createdAt });
            });
        });
        return Array.from(GROUPS.entries()).map(([date, files]) => ({ date, files }));
    }, [chat.selectedConversation?.messages, memo__usersMapMemo]);

    useEffect(() => {
        const NODE = REF__useRefMessages.current;
        if (!NODE) return;
        NODE.scrollTop = NODE.scrollHeight;
    }, [chat.selectedConversation?.messages?.length, chat.selectedConversationId]);

    useEffect(() => {
        setNameRenameState(chat.selectedConversation?.name ?? onConversationTitle(chat.selectedConversation));
        setNameGroupImageState(chat.selectedConversation?.image ?? null);
        setNamePanelState(null);
        setNameInviteEmailState('');
        setNameManageNoticeState('');
        setNameMentionedUsersState([]);
        setNameMentionQueryState(null);
        setNameMentionRangeState(null);
        setNameMobilePanelState(null);
    }, [chat.selectedConversationId]);

    useEffect(() => {
        if (!memo__hasLeftGroupMemo) return;
        setNameParticipantProfileState(null);
        setNameDeleteMessageState(null);
        setNamePreviewImageState(null);
    }, [memo__hasLeftGroupMemo]);

    useEffect(() => {
        if (!useNameComposerNoticeState) return undefined;
        const TIMER = window.setTimeout(() => setNameComposerNoticeState(''), 5000);
        return () => window.clearTimeout(TIMER);
    }, [useNameComposerNoticeState]);

    useEffect(() => () => {
        useNameAttachmentsState.forEach((attachment) => {
            if (attachment.url?.startsWith('blob:')) URL.revokeObjectURL(attachment.url);
        });
    }, []);

    const cb__showComposerNoticeCallback = (message) => {
        setNameComposerNoticeState('');
        window.setTimeout(() => setNameComposerNoticeState(message), 0);
    };


    const onOpenDeleteMessage = (message) => {
        if (!message || message.system || memo__hasLeftGroupMemo) return;
        setNameDeleteMessageState(message);
    };

    const onDeleteMessageForMe = () => {
        if (!useNameDeleteMessageState || !chat.selectedConversationId) return;
        chat.deleteMessageForMe(chat.selectedConversationId, useNameDeleteMessageState.id);
        setNameDeleteMessageState(null);
    };

    const onDeleteMessageForAll = () => {
        if (!useNameDeleteMessageState || !chat.selectedConversationId) return;
        if (useNameDeleteMessageState.senderId !== currentUserId) return;
        chat.deleteMessageForAll(chat.selectedConversationId, useNameDeleteMessageState.id);
        setNameDeleteMessageState(null);
    };

    const onClearChatForMe = () => {
        if (!chat.selectedConversationId) return;
        chat.clearConversationForMe(chat.selectedConversationId);
        setNameClearChatState(false);
        setNamePanelState(null);
    };

    const onDeleteConversationForMe = () => {
        if (!chat.selectedConversationId) return;
        chat.deleteConversation(chat.selectedConversationId);
        setNameDeleteConversationState(false);
        setNamePanelState(null);
    };

    const onSubmit = (event) => {
        event.preventDefault();
        const ACTIVE_MENTIONS = memo__isGroupMemo ? useNameMentionedUsersState : [];
        const MENTIONS = ACTIVE_MENTIONS
            .map((user) => `@${String(user.name ?? '').trim().split(/\s+/)[0]}`)
            .filter(Boolean).join(' ');
        const BODY = useNameMessageState.trim();
        const TEXT = [MENTIONS, BODY].filter(Boolean).join(' ');
        const SENT = chat.sendMessage({ text: TEXT, attachments: useNameAttachmentsState });
        if (!SENT) return;
        setNameMessageState('');
        setNameMentionedUsersState([]);
        setNameMentionQueryState(null);
        setNameMentionRangeState(null);
        setNameAttachmentsState([]);
        if (REF__useRefFileInput.current) REF__useRefFileInput.current.value = '';
    };

    const onDetectMention = (value, caret) => {
        if (!memo__isGroupMemo) {
            setNameMentionQueryState(null);
            setNameMentionRangeState(null);
            return;
        }
        const BEFORE = String(value).slice(0, caret);
        const MATCH = BEFORE.match(/(?:^|\s)@([^\s@]*)$/);
        if (!MATCH) {
            setNameMentionQueryState(null);
            setNameMentionRangeState(null);
            return;
        }
        const START = BEFORE.lastIndexOf('@');
        setNameMentionQueryState(MATCH[1] ?? '');
        setNameMentionRangeState({ start: START, end: caret });
    };

    const onMessageChange = (event) => {
        const VALUE = event.target.value;
        setNameMessageState(VALUE);
        onDetectMention(VALUE, event.target.selectionStart ?? VALUE.length);
    };

    const onSelectMention = (user) => {
        if (!user || !useNameMentionRangeState) return;
        const ALREADY_SELECTED = useNameMentionedUsersState.some((item) => item.id === user.id);
        const BEFORE = useNameMessageState.slice(0, useNameMentionRangeState.start);
        const AFTER = useNameMessageState.slice(useNameMentionRangeState.end);
        const NEXT_VALUE = `${BEFORE}${AFTER}`.replace(/\s{2,}/g, ' ');
        setNameMessageState(NEXT_VALUE);
        setNameMentionQueryState(null);
        setNameMentionRangeState(null);

        if (ALREADY_SELECTED) {
            const FIRST_NAME = String(user.name ?? '').trim().split(/\s+/)[0] || 'Ese participante';
            cb__showComposerNoticeCallback(`${FIRST_NAME} ya está mencionado.`);
        } else {
            setNameMentionedUsersState((current) => [...current, user]);
        }

        requestAnimationFrame(() => {
            const NODE = REF__useRefComposer.current;
            if (!NODE) return;
            const POSITION = Math.min(BEFORE.length, NEXT_VALUE.length);
            NODE.focus();
            NODE.selectionStart = NODE.selectionEnd = POSITION;
        });
    };

    const onRemoveMention = (userId) => {
        setNameMentionedUsersState((current) => current.filter((user) => user.id !== userId));
    };

    const onOpenCreateGroup = () => {
        setNameGroupNameState('');
        setNameGroupImageState(null);
        setNameSelectedUsersState([]);
        setNamePanelState('create');
    };

    const onOpenManageGroup = () => {
        setNameRenameState(chat.selectedConversation?.name ?? onConversationTitle(chat.selectedConversation));
        setNameGroupImageState(chat.selectedConversation?.image ?? null);
        setNameManageNoticeState('');
        setNamePanelState('manage');
    };

    const onGroupImageSelected = (event) => {
        const FILE = event.target.files?.[0];
        if (!FILE || !String(FILE.type ?? '').startsWith('image/')) {
            event.target.value = '';
            return;
        }
        setNameGroupImageState({
            id: `group-image-${Date.now()}`, name: FILE.name, size: FILE.size,
            type: FILE.type, url: URL.createObjectURL(FILE),
        });
        event.target.value = '';
    };

    const onRemoveGroupImage = () => {
        setNameGroupImageState(null);
    };

    const onFilesSelected = (event) => {
        const FILES = Array.from(event.target.files ?? []);
        if (FILES.length === 0) return;

        const USED = new Set(useNameAttachmentsState.map(onAttachmentSignature));
        (chat.selectedConversation?.messages ?? []).filter((message) => !message.archived && !(message.hiddenFor ?? []).includes(currentUserId)).forEach((message) => {
            (message.attachments ?? []).forEach((attachment) => USED.add(onAttachmentSignature(attachment)));
        });

        const ACCEPTED = [];
        const DUPLICATES = [];

        FILES.forEach((file) => {
            const SIGNATURE = onAttachmentSignature(file);
            if (USED.has(SIGNATURE)) {
                DUPLICATES.push(file.name);
                return;
            }
            USED.add(SIGNATURE);
            ACCEPTED.push(onAttachmentFromFile(file));
        });

        if (ACCEPTED.length > 0) setNameAttachmentsState((current) => [...current, ...ACCEPTED]);
        if (DUPLICATES.length > 0) {
            const LABEL = DUPLICATES.length === 1
                ? `El archivo “${DUPLICATES[0]}” ya fue agregado.`
                : `${DUPLICATES.length} archivos ya habían sido agregados.`;
            cb__showComposerNoticeCallback(LABEL);
        }

        event.target.value = '';
    };

    const onRemoveAttachment = (id) => {
        setNameAttachmentsState((current) => current.filter((attachment) => {
            if (attachment.id === id && attachment.url?.startsWith('blob:')) URL.revokeObjectURL(attachment.url);
            return attachment.id !== id;
        }));
    };

    const onCreateGroup = () => {
        const CREATOR = memo__usersMapMemo.get(currentUserId)?.name ?? 'Un participante';
        const CREATED = chat.createGroup({
            name: useNameGroupNameState, memberIds: useNameSelectedUsersState,
            creatorName: CREATOR, image: useNameGroupImageState,
        });
        if (!CREATED) return;
        setNameGroupNameState('');
        setNameGroupImageState(null);
        setNameSelectedUsersState([]);
        setNamePanelState(null);
    };

    const onSaveGroupName = () => {
        if (!memo__isGroupMemo || !memo__isCurrentUserAdminMemo) return false;
        const RENAMED = chat.renameConversation(chat.selectedConversationId, useNameRenameState);
        const IMAGE_UPDATED = chat.updateConversationImage(chat.selectedConversationId, useNameGroupImageState);
        return RENAMED || IMAGE_UPDATED;
    };

    const onInvite = () => {
        if (!chat.inviteParticipant(chat.selectedConversationId, useNameInviteEmailState)) return false;
        setNameInviteEmailState('');
        return true;
    };

    const onLeaveGroup = () => {
        const RESULT = chat.leaveGroup(chat.selectedConversationId);
        if (RESULT?.ok) {
            setNamePanelState(null);
            return;
        }
        if (RESULT?.reason === 'transfer-admin') {
            setNameManageNoticeState('Antes de salir del grupo designá al menos otro administrador.');
        }
    };

    const onConversationAvatar = (conversation, className = '') => {
        if (onIsGroup(conversation) && conversation?.image?.url) {
            return (
                <span className={`${BLOCK}__avatar ${BLOCK}__avatar_group ${className}`.trim()}>
                    <img src={conversation.image.url} alt={`Imagen de ${onConversationTitle(conversation)}`} />
                </span>
            );
        }
        return <span className={`${BLOCK}__avatar ${className}`.trim()}>{onInitials(onConversationTitle(conversation))}</span>;
    };

    const onRenderConversation = (conversation) => {
        const VISIBLE_MESSAGES = (conversation.messages ?? []).filter((message) => !(message.hiddenFor ?? []).includes(currentUserId));
        const LAST = VISIBLE_MESSAGES[VISIBLE_MESSAGES.length - 1];
        const ACTIVE = conversation.id === chat.selectedConversationId;
        const UNREAD = Number(conversation.unread ?? 0);
        const MENTIONS = onIsGroup(conversation) ? Number(conversation.mentions ?? 0) : 0;

        return (
            <button
                key={conversation.id}
                type="button"
                className={`${BLOCK}__conversation`}
                data-active={ACTIVE}
                data-pinned={Boolean(conversation.pinned)}
                data-unread={UNREAD > 0}
                onClick={() => { chat.selectConversation(conversation.id); setNameMobilePanelState(null); }}
            >
                {onConversationAvatar(conversation)}
                <span className={`${BLOCK}__conversation_text`}>
                    <strong>{onConversationTitle(conversation)}</strong>
                    <small>{LAST?.text || (LAST?.attachments?.length ? `${LAST.attachments.length} archivo(s)` : 'Sin mensajes todavía')}</small>
                </span>
                <span className={`${BLOCK}__conversation_meta`}>
                    <span className={`${BLOCK}__conversation_meta_top`}>
                        {conversation.pinned && (
                            <FaThumbtack className={`${BLOCK}__pin`} title="Chat fijado" />
                        )}
                        <small>{onTime(LAST?.createdAt)}</small>
                    </span>
                    <span className={`${BLOCK}__conversation_badges`}>
                        {MENTIONS > 0 && <i className={`${BLOCK}__mention_badge`}><FaAt />{MENTIONS}</i>}
                        {UNREAD > 0 && <b>{UNREAD}</b>}
                    </span>
                </span>
            </button>
        );
    };

    return (
        <section className={BLOCK} data-has-selection={Boolean(chat.selectedConversation)} data-left-group={memo__hasLeftGroupMemo}
            data-mobile-panel={useNameMobilePanelState ?? 'none'}>
            {useNameMobilePanelState && (
                <button type="button" className={`${BLOCK}__mobile_backdrop`}
                    onClick={() => setNameMobilePanelState(null)} aria-label="Cerrar panel lateral" />
            )}

            <aside className={`${BLOCK}__sidebar`} data-mobile-open={useNameMobilePanelState === 'conversations'}>
                <div className={`${BLOCK}__sidebar_head`}>
                    <div><span>Chat</span><h3>{title}</h3></div>
                    <div className={`${BLOCK}__sidebar_head_actions`}>
                        <button
                            type="button"
                            className={`${BLOCK}__icon_button`}
                            onClick={onOpenCreateGroup}
                            aria-label="Crear grupo"
                            title="Crear conversación grupal"
                        >
                            <FaUsers />
                        </button>
                        <button type="button" className={`${BLOCK}__icon_button ${BLOCK}__mobile_only`}
                            onClick={() => setNameMobilePanelState(null)} aria-label="Cerrar conversaciones" title="Cerrar conversaciones">
                            <FaTimes />
                        </button>
                    </div>
                </div>

                <label className={`${BLOCK}__search`}>
                    <FaSearch aria-hidden="true" />
                    <input
                        type="search"
                        value={useNameSearchState}
                        onChange={(event) => setNameSearchState(event.target.value)}
                        placeholder="Buscar conversación..."
                    />
                </label>

                <div className={`${BLOCK}__filters`} role="group" aria-label="Filtrar conversaciones">
                    <button type="button" data-active={useNameConversationFilterState === 'all'}
                        onClick={() => setNameConversationFilterState('all')}>
                        Todos <span>{memo__filterCountsMemo.all}</span>
                    </button>
                    <button type="button" data-active={useNameConversationFilterState === 'unread'}
                        onClick={() => setNameConversationFilterState('unread')}>
                        No leídos <span>{memo__filterCountsMemo.unread}</span>
                    </button>
                    <button type="button" data-active={useNameConversationFilterState === 'mentions'}
                        onClick={() => setNameConversationFilterState('mentions')}>
                        <FaAt /> Menciones <span>{memo__filterCountsMemo.mentions}</span>
                    </button>
                </div>

                <div className={`${BLOCK}__conversation_list`}>
                    {memo__conversationsMemo.length === 0 ? (
                        <div className={`${BLOCK}__search_empty`}>
                            <FaSearch />
                            <strong>No se encontraron conversaciones</strong>
                            <span>Probá con otro nombre o participante.</span>
                        </div>
                    ) : (
                        <>
                            {memo__conversationSectionsMemo.groups.length > 0 && (
                                <section className={`${BLOCK}__conversation_section`}>
                                    <div className={`${BLOCK}__conversation_divider`}>
                                        <span>Grupos</span><b>{memo__conversationSectionsMemo.groups.length}</b>
                                    </div>
                                    {memo__conversationSectionsMemo.groups.map(onRenderConversation)}
                                </section>
                            )}

                            {memo__conversationSectionsMemo.direct.length > 0 && (
                                <section className={`${BLOCK}__conversation_section`}>
                                    <div className={`${BLOCK}__conversation_divider`}>
                                        <span>Conversaciones</span><b>{memo__conversationSectionsMemo.direct.length}</b>
                                    </div>
                                    {memo__conversationSectionsMemo.direct.map(onRenderConversation)}
                                </section>
                            )}
                        </>
                    )}
                </div>
            </aside>

            <div className={`${BLOCK}__main`}>
                {chat.selectedConversation ? (
                    <>
                        <header className={`${BLOCK}__topbar`}>
                            <div className={`${BLOCK}__topbar_identity`}>
                                <button type="button" className={`${BLOCK}__icon_button ${BLOCK}__mobile_only ${BLOCK}__mobile_menu_button`}
                                    onClick={() => setNameMobilePanelState('conversations')} aria-label="Abrir conversaciones" title="Abrir conversaciones">
                                    <FaBars />
                                </button>
                                {onConversationAvatar(chat.selectedConversation)}
                                <div>
                                    <div className={`${BLOCK}__title_row`}>
                                        <h3>{onConversationTitle(chat.selectedConversation)}</h3>
                                    </div>
                                    <p>
                                        {memo__isGroupMemo
                                            ? `${memo__selectedMembersMemo.length} participantes`
                                            : memo__selectedMembersMemo.map((user) => user.name).join(' · ')}
                                    </p>
                                </div>
                            </div>
                            <div className={`${BLOCK}__topbar_actions`}>
                                {!memo__hasLeftGroupMemo && (
                                    <button type="button" className={`${BLOCK}__icon_button ${BLOCK}__mobile_only`}
                                        onClick={() => setNameMobilePanelState('participants')} aria-label="Ver participantes" title="Ver participantes">
                                        <FaUsers />
                                    </button>
                                )}
                                {!memo__hasLeftGroupMemo && (
                                    <button
                                        type="button"
                                        className={`${BLOCK}__icon_button`}
                                        data-active={Boolean(chat.selectedConversation.pinned)}
                                        onClick={() => chat.togglePin(chat.selectedConversationId)}
                                        aria-label={chat.selectedConversation.pinned ? 'Desfijar chat' : 'Fijar chat'}
                                        title={chat.selectedConversation.pinned ? 'Desfijar chat' : 'Fijar chat'}
                                    >
                                        <FaThumbtack />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={`${BLOCK}__icon_button`}
                                    onClick={onOpenManageGroup}
                                    aria-label={memo__hasLeftGroupMemo ? 'Opciones de conversación' : 'Gestionar conversación'}
                                    title={memo__hasLeftGroupMemo ? 'Limpiar o eliminar conversación' : 'Gestionar participantes, nombre e invitaciones'}
                                >
                                    <FaUsersCog />
                                </button>
                                <button
                                    type="button"
                                    className={`${BLOCK}__icon_button ${BLOCK}__close_conversation`}
                                    onClick={() => { setNamePanelState(null); chat.clearSelection(); }}
                                    aria-label="Cerrar conversación seleccionada"
                                    title="Cerrar conversación seleccionada"
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        </header>

                        <div className={`${BLOCK}__activity_stack`}>
                            {memo__typingUsersMemo.length > 0 && !memo__hasLeftGroupMemo && (
                                <div className={`${BLOCK}__typing_bar`} role="status" aria-live="polite">
                                    <div className={`${BLOCK}__typing_avatars`}>
                                        {memo__typingUsersMemo.slice(0, 3).map((user) => (
                                            <span key={user.id} className={`${BLOCK}__typing_avatar`}>{onInitials(user.name)}</span>
                                        ))}
                                    </div>
                                    <div className={`${BLOCK}__typing_copy`}>
                                        <strong>{memo__typingUsersMemo.length === 1
                                            ? `${memo__typingUsersMemo[0].name} está escribiendo`
                                            : `${memo__typingUsersMemo.slice(0, 2).map((user) => user.name).join(' y ')} están escribiendo`}</strong>
                                        <span className={`${BLOCK}__typing_dots`} aria-hidden="true"><i /><i /><i /></span>
                                    </div>
                                </div>
                            )}

                            {memo__pinnedMessageMemo && !memo__pinnedMessageMemo.archived && !(memo__pinnedMessageMemo.hiddenFor ?? []).includes(currentUserId) && (
                                <button type="button" className={`${BLOCK}__pinned_message`}
                                    onClick={() => document.getElementById(`${BLOCK}-message-${memo__pinnedMessageMemo.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
                                    <span className={`${BLOCK}__pinned_message_icon`}><FaThumbtack /></span>
                                    <span className={`${BLOCK}__pinned_message_copy`}>
                                        <small>Mensaje fijado</small>
                                        <span className={`${BLOCK}__pinned_message_line`}>
                                            <strong>{memo__usersMapMemo.get(memo__pinnedMessageMemo.senderId)?.name ?? 'Conversación'}</strong>
                                            <em>{memo__pinnedMessageMemo.text || memo__pinnedMessageMemo.attachments?.[0]?.name || 'Archivo adjunto'}</em>
                                        </span>
                                    </span>
                                    {!memo__hasLeftGroupMemo && (
                                        <span className={`${BLOCK}__pinned_message_remove`} role="button" tabIndex={0} title="Desfijar mensaje"
                                            onClick={(event) => { event.stopPropagation(); chat.togglePinnedMessage(chat.selectedConversation.id, memo__pinnedMessageMemo.id); }}
                                            onKeyDown={(event) => { if (event.key === 'Enter') { event.stopPropagation(); chat.togglePinnedMessage(chat.selectedConversation.id, memo__pinnedMessageMemo.id); } }}>
                                            <FaTimes />
                                        </span>
                                    )}
                                </button>
                            )}
                        </div>

                        <div ref={REF__useRefMessages} className={`${BLOCK}__messages`}>
                            {(chat.selectedConversation.messages ?? []).filter((message) => !message.archived && !(message.hiddenFor ?? []).includes(currentUserId)).map((message, index, list) => {
                                const USER = memo__usersMapMemo.get(message.senderId);
                                const OWN = message.senderId === currentUserId;
                                const STATUS = onStatus(message.status);
                                const PREVIOUS = list[index - 1];
                                const SHOW_DATE = !PREVIOUS || onDateKey(PREVIOUS.createdAt) !== onDateKey(message.createdAt);
                                const IS_PINNED = chat.selectedConversation?.pinnedMessageId === message.id;

                                return (
                                    <div key={message.id} className={`${BLOCK}__message_group`}>
                                    {SHOW_DATE && (
                                        <div className={`${BLOCK}__date_divider`}><span>{onDateGroupLabel(message.createdAt)}</span></div>
                                    )}
                                    <article id={`${BLOCK}-message-${message.id}`} className={`${BLOCK}__message`} data-own={OWN}
                                        data-system={Boolean(message.system)} data-event={message.event ?? undefined} data-pinned={IS_PINNED}>
                                        {!OWN && !message.system && <span className={`${BLOCK}__avatar`}>{onInitials(USER?.name)}</span>}
                                        <div className={`${BLOCK}__message_stack`}>
                                            <div className={`${BLOCK}__bubble`}>
                                            {!OWN && !message.system && <strong>{USER?.name ?? 'Usuario'}</strong>}
                                            {message.system && message.event === 'group-created' && <FaUsers className={`${BLOCK}__system_icon`} />}
                                            {message.text && <p>{onRenderMentionedText(message.text, memo__selectedMembersMemo, memo__isGroupMemo)}</p>}

                                            {(message.attachments ?? []).length > 0 && (
                                                <div className={`${BLOCK}__message_files`}>
                                                    {message.attachments.map((attachment) => (
                                                        <div key={attachment.id ?? attachment.name} className={`${BLOCK}__message_file`} data-image={onIsImageAttachment(attachment)}>
                                                            {onIsImageAttachment(attachment) ? (
                                                                memo__hasLeftGroupMemo ? (
                                                                    <div className={`${BLOCK}__message_image_preview ${BLOCK}__message_image_preview--readonly`}>
                                                                        <img src={attachment.url} alt={attachment.name} />
                                                                    </div>
                                                                ) : (
                                                                    <button type="button" className={`${BLOCK}__message_image_preview`}
                                                                        onClick={() => setNamePreviewImageState(attachment)} title={`Ver ${attachment.name}`}>
                                                                        <img src={attachment.url} alt={attachment.name} />
                                                                        <span><FaImage /> Ver imagen</span>
                                                                    </button>
                                                                )
                                                            ) : <FaFileAlt />}
                                                            <span><strong>{attachment.name}</strong><small>{onFormatBytes(attachment.size)}</small></span>
                                                            {!memo__hasLeftGroupMemo && (
                                                                <a href={attachment.url} download={attachment.name} className={`${BLOCK}__message_download`} title={`Descargar ${attachment.name}`}>
                                                                    <FaDownload />
                                                                </a>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            </div>
                                            {!message.system && (
                                                <>
                                                    {!memo__hasLeftGroupMemo && (
                                                        <div className={`${BLOCK}__message_quick_actions`}>
                                                            <button type="button" className={`${BLOCK}__message_pin_trigger`}
                                                                data-active={IS_PINNED}
                                                                onClick={() => chat.togglePinnedMessage(chat.selectedConversation.id, message.id)}
                                                                aria-label={IS_PINNED ? 'Desfijar mensaje' : 'Fijar mensaje'}
                                                                title={IS_PINNED ? 'Desfijar mensaje' : 'Fijar mensaje'}>
                                                                <FaThumbtack />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`${BLOCK}__message_delete_trigger`}
                                                                onClick={() => onOpenDeleteMessage(message)}
                                                                aria-label="Eliminar mensaje"
                                                                title="Eliminar mensaje"
                                                            >
                                                                <FaTimes />
                                                            </button>
                                                        </div>
                                                    )}
                                                    <span className={`${BLOCK}__message_meta`}>
                                                        <span>{onTime(message.createdAt)}</span>
                                                        {OWN && (
                                                            <em className={`${BLOCK}__status`} data-status={STATUS.className} title={STATUS.label}>
                                                                {STATUS.icon}<span>{STATUS.label}</span>
                                                            </em>
                                                        )}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </article>
                                    </div>
                                );
                            })}
                        </div>

                        {memo__hasLeftGroupMemo ? (
                            <div className={`${BLOCK}__left_group_notice`} role="status">
                                <FaSignOutAlt />
                                <div>
                                    <strong>Ya no participás de este grupo</strong>
                                    <span>Podés consultar el historial, pero ya no podés enviar mensajes ni archivos.</span>
                                </div>
                            </div>
                        ) : (
                        <form className={`${BLOCK}__composer`} onSubmit={onSubmit}>
                            {useNameAttachmentsState.length > 0 && (
                                <div className={`${BLOCK}__pending_files`}>
                                    {useNameAttachmentsState.map((attachment) => (
                                        <div key={attachment.id} className={`${BLOCK}__pending_file`}>
                                            <FaFileAlt />
                                            <span><strong>{attachment.name}</strong><small>{onFormatBytes(attachment.size)}</small></span>
                                            <button
                                                type="button"
                                                onClick={() => onRemoveAttachment(attachment.id)}
                                                aria-label={`Quitar ${attachment.name}`}
                                                title={`Quitar ${attachment.name}`}
                                            >
                                                <FaTimes />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className={`${BLOCK}__composer_row`}>
                                <input ref={REF__useRefFileInput} type="file" multiple hidden onChange={onFilesSelected} />
                                <button
                                    type="button"
                                    className={`${BLOCK}__files_button`}
                                    onClick={() => setNamePanelState('files')}
                                    aria-label="Ver archivos de la conversación"
                                    title="Ver archivos de la conversación"
                                >
                                    <FaFolderOpen />
                                </button>
                                <button
                                    type="button"
                                    className={`${BLOCK}__attach_button`}
                                    onClick={() => REF__useRefFileInput.current?.click()}
                                    aria-label="Adjuntar archivos"
                                    title="Adjuntar uno o varios archivos"
                                >
                                    <FaPaperclip />
                                </button>
                                <div className={`${BLOCK}__composer_message`}>
                                    {memo__isGroupMemo && useNameMentionedUsersState.length > 0 && (
                                        <div className={`${BLOCK}__mention_tokens`} aria-label="Participantes mencionados">
                                            {useNameMentionedUsersState.map((user) => (
                                                <span key={user.id} className={`${BLOCK}__mention_token`}>
                                                    <FaAt aria-hidden="true" />
                                                    <strong>{String(user.name ?? '').trim().split(/\s+/)[0]}</strong>
                                                    <button type="button" onClick={() => onRemoveMention(user.id)}
                                                        aria-label={`Quitar mención a ${user.name}`} title={`Quitar mención a ${user.name}`}>
                                                        <FaTimes />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    {memo__mentionCandidatesMemo.length > 0 && (
                                        <div className={`${BLOCK}__mention_suggestions`} role="listbox" aria-label="Participantes para mencionar">
                                            {memo__mentionCandidatesMemo.map((user) => {
                                                const ALREADY_SELECTED = useNameMentionedUsersState.some((item) => item.id === user.id);
                                                return (
                                                    <button key={user.id} type="button" role="option"
                                                        aria-selected={ALREADY_SELECTED}
                                                        className={`${BLOCK}__mention_suggestion`}
                                                        onMouseDown={(event) => { event.preventDefault(); onSelectMention(user); }}>
                                                        <span className={`${BLOCK}__avatar`}>{onInitials(user.name)}</span>
                                                        <span>
                                                            <strong>{user.name}</strong>
                                                            <small>{ALREADY_SELECTED ? 'Ya mencionado' : (user.role ?? 'Participante')}</small>
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    <textarea
                                        ref={REF__useRefComposer}
                                        rows={1}
                                        value={useNameMessageState}
                                        onChange={onMessageChange}
                                        onClick={(event) => onDetectMention(event.currentTarget.value, event.currentTarget.selectionStart ?? 0)}
                                        onKeyUp={(event) => {
                                            if (!['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) {
                                                onDetectMention(event.currentTarget.value, event.currentTarget.selectionStart ?? 0);
                                            }
                                        }}
                                        onKeyDown={(event) => {
                                            if (memo__mentionCandidatesMemo.length > 0 && event.key === 'Escape') {
                                                event.preventDefault();
                                                setNameMentionQueryState(null);
                                                setNameMentionRangeState(null);
                                                return;
                                            }
                                            if (memo__mentionCandidatesMemo.length > 0 && (event.key === 'Enter' || event.key === 'Tab')) {
                                                event.preventDefault();
                                                onSelectMention(memo__mentionCandidatesMemo[0]);
                                                return;
                                            }
                                            if (event.key === 'Enter' && !event.shiftKey) {
                                                event.preventDefault();
                                                event.currentTarget.form?.requestSubmit();
                                            }
                                        }}
                                        placeholder={memo__isGroupMemo ? "Escribí un mensaje... Usá @ para mencionar" : "Escribí un mensaje..."}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className={`${BLOCK}__send_button`}
                                    disabled={!useNameMessageState.trim() && useNameMentionedUsersState.length === 0 && useNameAttachmentsState.length === 0}
                                    aria-label="Enviar mensaje"
                                    title="Enviar mensaje"
                                >
                                    <FaPaperPlane />
                                </button>
                            </div>

                            {useNameComposerNoticeState && (
                                <div className={`${BLOCK}__composer_notice`} role="status">
                                    {useNameComposerNoticeState}
                                </div>
                            )}
                        </form>
                        )}
                    </>
                ) : (
                    <div className={`${BLOCK}__empty`}>
                        <FaUsers />
                        <strong>No hay conversación seleccionada</strong>
                        <span>{emptyText || 'Elegí una conversación del panel izquierdo para comenzar.'}</span>
                    </div>
                )}
            </div>

            <aside className={`${BLOCK}__members`} data-mobile-open={useNameMobilePanelState === 'participants'}>
                {chat.selectedConversation && !memo__hasLeftGroupMemo && (<>
                <div className={`${BLOCK}__members_mobile_head`}>
                    <div><span>Chat</span><strong>Participantes</strong></div>
                    <button type="button" className={`${BLOCK}__icon_button ${BLOCK}__mobile_only`}
                        onClick={() => setNameMobilePanelState(null)} aria-label="Cerrar participantes" title="Cerrar participantes">
                        <FaTimes />
                    </button>
                </div>
                <span className={`${BLOCK}__members_label`}>Participantes</span>
                <strong>{memo__selectedMembersMemo.length || 0}</strong>
                <div className={`${BLOCK}__members_list`}>
                    {memo__selectedMembersMemo.map((user) => {
                        const IS_ADMIN = memo__adminIdsMemo.includes(user.id);
                        return (
                            <button
                                key={user.id}
                                type="button"
                                className={`${BLOCK}__member`}
                                onClick={() => setNameParticipantProfileState({ ...user, isAdmin: IS_ADMIN })}
                                aria-label={`Ver perfil de ${user.name}`}
                                title={`Ver perfil de ${user.name}`}
                            >
                                <span className={`${BLOCK}__avatar`}>{onInitials(user.name)}</span>
                                <span className={`${BLOCK}__member_content`}>
                                    <strong className={`${BLOCK}__member_name`}>
                                        {user.name}{IS_ADMIN && <FaCrown className={`${BLOCK}__admin_crown`} title="Admin del grupo" />}
                                    </strong>
                                    <small>{IS_ADMIN ? 'Admin del grupo' : (user.role ?? 'Participante')}</small>
                                </span>
                                <i data-online={Boolean(user.online)} />
                            </button>
                        );
                    })}
                </div>
                </>)}
            </aside>


            <AppModal
                isOpen={Boolean(useNameParticipantProfileState) && !memo__hasLeftGroupMemo}
                onClose={() => setNameParticipantProfileState(null)}
                title="Perfil del participante"
                description={useNameParticipantProfileState?.name ?? ''}
                size="md"
                className={`${BLOCK}__modal ${BLOCK}__participant_profile_modal`}
            >
                {useNameParticipantProfileState && (
                    <div className={`${BLOCK}__participant_profile`}>
                        <div className={`${BLOCK}__participant_profile_head`}>
                            <span className={`${BLOCK}__participant_profile_avatar`}>
                                {onInitials(useNameParticipantProfileState.name)}
                            </span>
                            <div className={`${BLOCK}__participant_profile_identity`}>
                                <strong>
                                    {useNameParticipantProfileState.name}
                                    {useNameParticipantProfileState.isAdmin && (
                                        <FaCrown className={`${BLOCK}__admin_crown`} title="Admin del grupo" />
                                    )}
                                </strong>
                                <div className={`${BLOCK}__participant_profile_meta`}>
                                    <span className={`${BLOCK}__participant_profile_role`}>
                                        {useNameParticipantProfileState.role ?? 'Participante'}
                                    </span>
                                    <small className={`${BLOCK}__participant_profile_status`} data-online={Boolean(useNameParticipantProfileState.online)}>
                                        {useNameParticipantProfileState.online ? 'En línea' : 'Desconectado'}
                                    </small>
                                </div>
                            </div>
                        </div>

                        {useNameParticipantProfileState.description && (
                            <section className={`${BLOCK}__participant_profile_description`}>
                                <span>Descripción</span>
                                <p>{useNameParticipantProfileState.description}</p>
                            </section>
                        )}

                        <div className={`${BLOCK}__participant_profile_grid`}>
                            {useNameParticipantProfileState.team && (
                                <div><FaIdBadge /><span><small>Equipo / área</small><strong>{useNameParticipantProfileState.team}</strong></span></div>
                            )}
                            {useNameParticipantProfileState.email && (
                                <div><FaEnvelope /><span><small>Correo</small><strong>{useNameParticipantProfileState.email}</strong></span></div>
                            )}
                            {useNameParticipantProfileState.phone && (
                                <div><FaPhone /><span><small>Teléfono</small><strong>{useNameParticipantProfileState.phone}</strong></span></div>
                            )}
                            {useNameParticipantProfileState.location && (
                                <div><FaMapMarkerAlt /><span><small>Ubicación</small><strong>{useNameParticipantProfileState.location}</strong></span></div>
                            )}
                        </div>
                    </div>
                )}
            </AppModal>

            <AppModal
                isOpen={useNamePanelState === 'create'}
                onClose={() => setNamePanelState(null)}
                title="Crear conversación grupal"
                description="Definí el nombre y seleccioná los participantes del nuevo grupo."
                size="lg"
                className={`${BLOCK}__modal`}
                footer={(
                    <div className={`${BLOCK}__modal_footer_layout ${BLOCK}__modal_footer_layout_create`}>
                        <div className={`${BLOCK}__modal_footer_group`}>
                            <AppButton variant="ghost" tone="neutral" className={`${BLOCK}__cancel_action`} onClick={() => setNamePanelState(null)}>Cancelar</AppButton>
                        </div>
                        <div className={`${BLOCK}__modal_footer_group ${BLOCK}__modal_footer_group_end`}>
                            <AppButton
                                tone="accent"
                                onClick={onCreateGroup}
                                isDisabled={!useNameGroupNameState.trim() || useNameSelectedUsersState.length < 2}
                            >
                                Crear grupo
                            </AppButton>
                        </div>
                    </div>
                )}
            >
                <div className={`${BLOCK}__modal_content`}>
                    <FieldInput
                        id="chat_group_name"
                        label="Nombre del grupo"
                        value={useNameGroupNameState}
                        onChange={(event) => setNameGroupNameState(event.target.value)}
                        placeholder="Ej. Equipo Producto"
                    />

                    <section className={`${BLOCK}__group_image_editor`}>
                        <div className={`${BLOCK}__group_image_preview`}>
                            {useNameGroupImageState?.url
                                ? <img src={useNameGroupImageState.url} alt="Vista previa del grupo" />
                                : <FaUsers />}
                        </div>
                        <div className={`${BLOCK}__group_image_content`}>
                            <strong>Imagen del grupo</strong>
                            <span>Podés agregarla ahora o actualizarla más adelante.</span>
                            <div className={`${BLOCK}__group_image_actions`}>
                                <input ref={REF__useRefGroupImageInput} type="file" accept="image/*" hidden onChange={onGroupImageSelected} />
                                <AppButton variant="outline" tone="accent" icon={<FaImage />}
                                    onClick={() => REF__useRefGroupImageInput.current?.click()}>
                                    {useNameGroupImageState ? 'Cambiar imagen' : 'Agregar imagen'}
                                </AppButton>
                                {useNameGroupImageState && (
                                    <AppButton variant="ghost" tone="danger" icon={<FaTrashAlt />} onClick={onRemoveGroupImage}>
                                        Quitar
                                    </AppButton>
                                )}
                            </div>
                        </div>
                    </section>

                    <section className={`${BLOCK}__modal_section`}>
                        <div className={`${BLOCK}__modal_section_head`}>
                            <div><h3>Participantes</h3><p>Seleccioná al menos dos personas para crear un grupo.</p></div>
                            <span>{useNameSelectedUsersState.length} seleccionados</span>
                        </div>

                        <div className={`${BLOCK}__participant_choices`}>
                            {users.filter((user) => user.id !== currentUserId).map((user) => (
                                <div key={user.id} className={`${BLOCK}__participant_choice`} data-selected={useNameSelectedUsersState.includes(user.id)}>
                                    <FieldCheckbox
                                        id={`chat_group_user_${user.id}`}
                                        text={(
                                            <span className={`${BLOCK}__participant_choice_text`}>
                                                <strong>{user.name}</strong><small>{user.role ?? 'Participante'}</small>
                                            </span>
                                        )}
                                        isChecked={useNameSelectedUsersState.includes(user.id)}
                                        onChange={(event) => {
                                            setNameSelectedUsersState((current) => event.target.checked
                                                ? [...current, user.id]
                                                : current.filter((id) => id !== user.id));
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </AppModal>

            <AppModal
                isOpen={useNamePanelState === 'manage'}
                onClose={() => setNamePanelState(null)}
                title="Gestionar chat"
                description={onConversationTitle(chat.selectedConversation)}
                size="lg"
                className={`${BLOCK}__modal`}
                footer={(
                    <div className={`${BLOCK}__modal_footer_layout`}>
                        <div className={`${BLOCK}__modal_footer_group`}>
                            {memo__isGroupMemo && !memo__hasLeftGroupMemo && (
                                <AppButton
                                    variant="soft" tone="danger" icon={<FaSignOutAlt />}
                                    className={`${BLOCK}__leave_group_action`} onClick={onLeaveGroup}
                                >
                                    Salir del grupo
                                </AppButton>
                            )}
                            <AppButton variant="soft" tone="warn" icon={<FaBroom />}
                                className={`${BLOCK}__clear_chat_action`} onClick={() => setNameClearChatState(true)}>
                                Limpiar chat
                            </AppButton>
                            {memo__hasLeftGroupMemo && (
                                <AppButton variant="soft" tone="danger" icon={<FaTrashAlt />}
                                    className={`${BLOCK}__delete_conversation_action`} onClick={() => setNameDeleteConversationState(true)}>
                                    Eliminar conversación
                                </AppButton>
                            )}
                        </div>
                        <div className={`${BLOCK}__modal_footer_group ${BLOCK}__modal_footer_group_end`}>
                            {memo__isGroupMemo && memo__isCurrentUserAdminMemo && !memo__hasLeftGroupMemo && useNameInviteEmailState.trim() && (
                                <AppButton variant="outline" tone="accent" onClick={onInvite} icon={<FaEnvelope />}>
                                    Enviar invitación
                                </AppButton>
                            )}
                            {memo__isGroupMemo && memo__isCurrentUserAdminMemo && !memo__hasLeftGroupMemo && (
                                <AppButton tone="accent" onClick={() => {
                                    if (onSaveGroupName()) setNamePanelState(null);
                                }} isDisabled={!useNameRenameState.trim()}>
                                    Guardar cambios
                                </AppButton>
                            )}
                        </div>
                    </div>
                )}
            >
                <div className={`${BLOCK}__modal_content`}>
                    {useNameManageNoticeState && <div className={`${BLOCK}__manage_notice`}>{useNameManageNoticeState}</div>}
                    {memo__hasLeftGroupMemo && (
                        <div className={`${BLOCK}__readonly_notice`}>
                            <FaSignOutAlt /><div><strong>Ya no participás de este grupo</strong><span>Solo podés consultar el historial, limpiar este chat o eliminar la conversación de tu lista.</span></div>
                        </div>
                    )}

                    {memo__isGroupMemo && !memo__hasLeftGroupMemo && (
                        <section className={`${BLOCK}__modal_section`}>
                            <div className={`${BLOCK}__modal_section_head`}>
                                <div>
                                    <h3>Información del grupo</h3>
                                    <p>{memo__isCurrentUserAdminMemo ? 'Tenés permisos para administrar este grupo.' : 'Sos participante de este grupo.'}</p>
                                </div>
                            </div>
                            <FieldInput
                                id="chat_group_rename"
                                label="Nombre del grupo"
                                value={useNameRenameState}
                                onChange={(event) => setNameRenameState(event.target.value)}
                                placeholder="Nombre del grupo"
                                isDisabled={!memo__isCurrentUserAdminMemo}
                            />
                            <div className={`${BLOCK}__group_image_editor ${BLOCK}__group_image_editor_manage`}>
                                <div className={`${BLOCK}__group_image_preview`}>
                                    {useNameGroupImageState?.url
                                        ? <img src={useNameGroupImageState.url} alt="Imagen actual del grupo" />
                                        : <FaUsers />}
                                </div>
                                <div className={`${BLOCK}__group_image_content`}>
                                    <strong>Imagen del grupo</strong>
                                    <span>{memo__isCurrentUserAdminMemo ? 'Actualizá la imagen visible para todos los participantes.' : 'Imagen actual de la conversación.'}</span>
                                    {memo__isCurrentUserAdminMemo && (
                                        <div className={`${BLOCK}__group_image_actions`}>
                                            <input ref={REF__useRefGroupImageInput} type="file" accept="image/*" hidden onChange={onGroupImageSelected} />
                                            <AppButton variant="outline" tone="accent" icon={<FaImage />}
                                                onClick={() => REF__useRefGroupImageInput.current?.click()}>
                                                {useNameGroupImageState ? 'Cambiar imagen' : 'Agregar imagen'}
                                            </AppButton>
                                            {useNameGroupImageState && (
                                                <AppButton variant="ghost" tone="danger" icon={<FaTrashAlt />} onClick={onRemoveGroupImage}>
                                                    Quitar
                                                </AppButton>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>
                    )}

                    {!memo__hasLeftGroupMemo && <section className={`${BLOCK}__modal_section`}>
                        <div className={`${BLOCK}__modal_section_head`}>
                            <div><h3>Participantes</h3><p>Administrá las personas que forman parte de esta conversación.</p></div>
                            <span>{memo__selectedMembersMemo.length}</span>
                        </div>

                        <div className={`${BLOCK}__manage_members`}>
                            {memo__selectedMembersMemo.map((user) => {
                                const IS_ADMIN = memo__adminIdsMemo.includes(user.id);
                                return (
                                    <div key={user.id}>
                                        <span className={`${BLOCK}__avatar`}>{onInitials(user.name)}</span>
                                        <span>
                                            <strong className={`${BLOCK}__member_name`}>
                                                {user.name}{IS_ADMIN && <FaCrown className={`${BLOCK}__admin_crown`} title="Admin del grupo" />}
                                            </strong>
                                            <small>{IS_ADMIN ? 'Admin del grupo' : user.role}</small>
                                        </span>
                                        <span className={`${BLOCK}__member_actions`}>
                                            {memo__isCurrentUserAdminMemo && !IS_ADMIN && user.id !== currentUserId && (
                                                <AppButton
                                                    variant="soft"
                                                    tone="warn"
                                                    size="sm"
                                                    className={`${BLOCK}__admin_action`}
                                                    isIconOnly
                                                    aria-label={`Hacer administrador a ${user.name}`}
                                                    title={`Hacer administrador a ${user.name}`}
                                                    icon={<FaCrown />}
                                                    onClick={() => chat.transferAdmin(chat.selectedConversationId, user.id)}
                                                />
                                            )}
                                            {memo__isCurrentUserAdminMemo && user.id !== currentUserId && (
                                                <AppButton
                                                    variant="soft"
                                                    tone="danger"
                                                    size="sm"
                                                    className={`${BLOCK}__remove_member_action`}
                                                    isIconOnly
                                                    aria-label={`Quitar a ${user.name}`}
                                                    title={`Quitar a ${user.name}`}
                                                    icon={<FaUserMinus />}
                                                    onClick={() => chat.removeParticipant(chat.selectedConversationId, user.id)}
                                                />
                                            )}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </section>}

                    {!memo__hasLeftGroupMemo && memo__isCurrentUserAdminMemo && memo__availableUsersMemo.length > 0 && (
                        <section className={`${BLOCK}__modal_section`}>
                            <h3>Agregar participante</h3>
                            <div className={`${BLOCK}__add_people`}>
                                {memo__availableUsersMemo.map((user) => (
                                    <AppButton
                                        key={user.id}
                                        variant="soft"
                                        tone="accent"
                                        size="sm"
                                        icon={<FaUserPlus />}
                                        data-tooltip-id={TOOLTIP_ID}
                                        data-tooltip-content={`Agregar a ${user.name} al grupo`}
                                        onClick={() => chat.addParticipant(chat.selectedConversationId, user.id)}
                                    >
                                        {user.name}
                                    </AppButton>
                                ))}
                            </div>
                        </section>
                    )}

                    {!memo__hasLeftGroupMemo && memo__isCurrentUserAdminMemo && (
                        <section className={`${BLOCK}__modal_section`}>
                            <h3>Enviar invitación</h3>
                            <FieldInput
                                id="chat_invite_email"
                                type="email"
                                value={useNameInviteEmailState}
                                onChange={(event) => setNameInviteEmailState(event.target.value)}
                                placeholder="persona@empresa.com"
                                iconStart={<FaEnvelope />}
                            />

                            {(chat.selectedConversation?.invitations ?? []).length > 0 && (
                                <div className={`${BLOCK}__invites`}>
                                    {(chat.selectedConversation.invitations ?? []).map((email) => (
                                        <span key={email}>{email}<small>Pendiente</small></span>
                                    ))}
                                </div>
                            )}
                        </section>
                    )}
                </div>
                <ReactTooltip
                    id={TOOLTIP_ID}
                    place="top"
                    delayShow={120}
                    positionStrategy="fixed"
                    className={`${BLOCK}__participant_tooltip`}
                    style={{ zIndex: 2147483647 }}
                />
            </AppModal>


            <AppModal
                isOpen={useNamePanelState === 'files'}
                onClose={() => setNamePanelState(null)}
                title="Archivos de la conversación"
                description={onConversationTitle(chat.selectedConversation)}
                size="xl"
                position="center"
                className={`${BLOCK}__modal ${BLOCK}__files_modal`}
            >
                <div className={`${BLOCK}__modal_content`}>
                    {memo__filesByDateMemo.length === 0 ? (
                        <div className={`${BLOCK}__files_empty`}>
                            <FaFolderOpen />
                            <strong>Todavía no hay archivos</strong>
                            <span>Los archivos enviados en esta conversación aparecerán acá ordenados por fecha.</span>
                        </div>
                    ) : memo__filesByDateMemo.map((group) => (
                        <section key={group.date} className={`${BLOCK}__files_group`}>
                            <div className={`${BLOCK}__files_date`}><span>{group.date}</span><b>{group.files.length}</b></div>
                            <div className={`${BLOCK}__files_list`}>
                                {group.files.map((file, index) => (
                                    <div key={`${file.id ?? file.name}-${index}`} className={`${BLOCK}__files_item`} data-image={onIsImageAttachment(file)}>
                                        {onIsImageAttachment(file) ? (
                                            <button type="button" className={`${BLOCK}__files_thumb`} onClick={() => setNamePreviewImageState(file)} title={`Ver ${file.name}`}>
                                                <img src={file.url} alt={file.name} />
                                            </button>
                                        ) : <span className={`${BLOCK}__files_icon`}><FaFileAlt /></span>}
                                        <span className={`${BLOCK}__files_info`}>
                                            <strong>{file.name}</strong>
                                            <small>{onFormatBytes(file.size)} · {file.senderName} · {onTime(file.createdAt)}</small>
                                        </span>
                                        <a href={file.url} download={file.name} className={`${BLOCK}__files_download`} title={`Descargar ${file.name}`}><FaDownload /></a>
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </AppModal>

            <AppModal
                isOpen={Boolean(useNameDeleteMessageState) && !memo__hasLeftGroupMemo}
                onClose={() => setNameDeleteMessageState(null)}
                title="Eliminar mensaje"
                description={useNameDeleteMessageState?.senderId === currentUserId
                    ? 'Elegí dónde querés eliminar este mensaje.'
                    : 'Este mensaje solo puede eliminarse de tu vista.'}
                size="sm"
                className={`${BLOCK}__modal ${BLOCK}__delete_message_app_modal`}
                footer={(
                    <div className={`${BLOCK}__delete_message_footer`}>
                        <AppButton variant="outline" tone="danger" icon={<FaTrashAlt />}
                            className={`${BLOCK}__delete_for_me_action`} onClick={onDeleteMessageForMe}>
                            Eliminar para mí
                        </AppButton>
                        <AppButton
                            variant="solid" tone="danger" icon={<FaTrashAlt />}
                            className={`${BLOCK}__delete_for_all_action`} onClick={onDeleteMessageForAll}
                            isDisabled={useNameDeleteMessageState?.senderId !== currentUserId}
                            title={useNameDeleteMessageState?.senderId !== currentUserId ? 'Solo podés eliminar para todos tus propios mensajes.' : 'Eliminar este mensaje para todos.'}
                        >
                            Eliminar para todos
                        </AppButton>
                    </div>
                )}
            >
                <div className={`${BLOCK}__delete_message_preview`}>
                    <span>Mensaje seleccionado</span>
                    <strong>{useNameDeleteMessageState?.text?.trim() || ((useNameDeleteMessageState?.attachments ?? []).length > 0 ? 'Archivo adjunto' : 'Mensaje')}</strong>
                </div>
            </AppModal>

            <AppModal
                isOpen={useNameClearChatState}
                onClose={() => setNameClearChatState(false)}
                title="Limpiar chat"
                description="El historial se ocultará solamente para vos."
                size="sm"
                className={`${BLOCK}__modal`}
                footer={(
                    <AppButton variant="solid" tone="warn" icon={<FaBroom />}
                        className={`${BLOCK}__clear_chat_confirm_action`} onClick={onClearChatForMe}>
                        Limpiar para mí
                    </AppButton>
                )}
            >
                <div className={`${BLOCK}__clear_chat_preview`}>
                    <span className={`${BLOCK}__clear_chat_broom`} aria-hidden="true"><FaBroom /></span>
                    <div>
                        <strong>Limpiar historial local</strong>
                        <span>La conversación seguirá disponible para los demás participantes.</span>
                    </div>
                </div>
            </AppModal>

            <AppModal
                isOpen={useNameDeleteConversationState}
                onClose={() => setNameDeleteConversationState(false)}
                title="Eliminar conversación"
                description="Esta conversación dejará de aparecer en tu lista y no podrás volver a acceder desde este demo."
                size="sm"
                className={`${BLOCK}__modal`}
                footer={(
                    <AppButton variant="solid" tone="danger" icon={<FaTrashAlt />}
                        className={`${BLOCK}__delete_conversation_confirm_action`} onClick={onDeleteConversationForMe}>
                        Eliminar conversación
                    </AppButton>
                )}
            >
                <div className={`${BLOCK}__clear_chat_preview`}>
                    <span className={`${BLOCK}__clear_chat_broom`} aria-hidden="true"><FaTrashAlt /></span>
                    <div>
                        <strong>Eliminar de mi lista</strong>
                        <span>El historial completo dejará de estar disponible para vos.</span>
                    </div>
                </div>
            </AppModal>

            <AppLightbox
                items={useNamePreviewImageState ? [{
                    src: useNamePreviewImageState.url,
                    alt: useNamePreviewImageState.name,
                    title: useNamePreviewImageState.name,
                }] : []}
                index={0}
                isOpen={Boolean(useNamePreviewImageState)}
                onClose={() => setNamePreviewImageState(null)}
                onChangeIndex={() => {}}
                hasCounter={false}
            />

        </section>
    );
};
